/**
 * Stato del manuale: aperto/chiuso e sezione corrente. Sta sopra il router, quindi legge la
 * posizione da window.location e il ruolo dalla cache delle query (già riempita dai loader).
 */
import type { UtenteCorrente } from '@aulaviva/shared';
import { ManualeDialog } from '@aulaviva/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { chiaveMe } from '@/api/auth';
import { manuale as t } from '@/testi/manuale';
import { frammentoDa, sezionePerPosizione, sezioniPerRuolo } from './contesto';

interface StatoManuale {
  aperto: boolean;
  /** Apre il manuale: sulla sezione indicata, oppure su quella della pagina corrente. */
  apri: (sezioneId?: string) => void;
  chiudi: () => void;
}

const Contesto = createContext<StatoManuale | null>(null);

function inCampoDiTesto(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function ManualeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const [sezioneId, setSezioneId] = useState<string | null>(null);
  const [ruolo, setRuolo] = useState<UtenteCorrente['ruolo'] | null>(null);

  const apri = useCallback(
    (id?: string) => {
      const utente = qc.getQueryData<UtenteCorrente | null>(chiaveMe) ?? null;
      const r = utente?.ruolo ?? null;
      setRuolo(r);
      const { pathname, search, hash } = window.location;
      setSezioneId(id ?? sezionePerPosizione(pathname, frammentoDa(search, hash), r).id);
      setAperto(true);
    },
    [qc],
  );
  const chiudi = useCallback(() => setAperto(false), []);

  // Scorciatoia: "?" fuori dai campi di testo.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey && !inCampoDiTesto(e.target)) {
        e.preventDefault();
        apri();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [apri]);

  const sezioni = useMemo(() => sezioniPerRuolo(ruolo), [ruolo]);
  const valore = useMemo(() => ({ aperto, apri, chiudi }), [aperto, apri, chiudi]);

  return (
    <Contesto.Provider value={valore}>
      {children}
      <ManualeDialog open={aperto} onOpenChange={setAperto} sezioni={sezioni} sezioneId={sezioneId} onSezioneChange={setSezioneId} testi={t.dialog} />
    </Contesto.Provider>
  );
}

export function useManuale(): StatoManuale {
  const c = useContext(Contesto);
  if (!c) throw new Error('useManuale fuori da ManualeProvider');
  return c;
}
